import React from "react";
import { Activity } from "lucide-react";

/**
 * Live readings streamed in by the Cocoon Health Sync app, rendered inside an
 * existing Detailed Results card. Every figure is labelled with the device it
 * came from so a wearable number is never mistaken for a clinic measurement.
 * A metric the device never sent renders as a placeholder — never a guess.
 */

// [key, label, unit, decimals]
export const RECOVERY_WEARABLE_FIELDS = [
  ["resting_heart_rate", "Resting HR", "bpm", 0],
  ["hrv_ms", "HRV", "ms", 0],
];

export const BIOMETRIC_WEARABLE_FIELDS = [
  ["daily_steps", "Daily steps", "", 0],
];

const fmtNumber = (value, decimals) =>
  new Intl.NumberFormat("en-GB", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);

const fmtWhen = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
};

export default function WearableMetrics({ metrics, fields, testId }) {
  const present = fields.filter(([k]) => metrics?.[k]?.value !== undefined && metrics?.[k]?.value !== null);
  // Name the device that produced the readings (Fitbit, Garmin, Samsung
  // Health …). Older syncs carry no stamp, so the label stays generic.
  const device = present.length ? metrics[present[0][0]].device_name : null;

  return (
    <div className="mt-4 pt-3 border-t border-border/60" data-testid={testId}>
      <p className="tracking-overline text-[10px] text-muted-foreground flex items-center gap-1.5">
        <Activity className="w-3 h-3" />
        {`FROM ${(device || "Wearable device").toUpperCase()}`}
      </p>

      {present.length === 0 ? (
        <p className="text-[11px] text-muted-foreground mt-1.5" data-testid={`${testId}-empty`}>
          No wearable data — sync the Cocoon Health Sync app to see it here.
        </p>
      ) : (
        <div className="flex flex-wrap gap-2 mt-2">
          {present.map(([key, label, unit, decimals]) => {
            const m = metrics[key];
            const when = fmtWhen(m.measured_at || m.synced_at);
            return (
              <span
                key={key}
                className="rounded-xl border border-border bg-muted/40 px-3 py-2 text-[11px] leading-tight"
                data-testid={`${testId}-${key}`}
              >
                <span className="text-muted-foreground block">{label}</span>
                <span className="font-medium text-xs">
                  {fmtNumber(m.value, decimals)}{unit ? ` ${unit}` : ""}
                </span>
                {when && <span className="text-muted-foreground block text-[10px]">{when}</span>}
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}
